import { makeAutoObservable, runInAction } from 'mobx';
import { ulid } from 'ulid';
import logger from '../helpers/logger';
import { store } from './store';
import enumHelper from '../helpers/enums';
import { v4 as guid } from 'uuid';
import passKeyHelper from '../helpers/passKey';
import sqlite from '../helpers/sqlite';
import { ApiCallServiceType } from '../types/ApiCall';
import { ReceiptType } from '../typegen/receipt-type';
import KioAppHelper from '../helpers/kioApp';

export default class SessionStore {
	private _sessionId: string | undefined = undefined;
	private _legacyId: string | undefined = undefined;
	private _isSessionEnding: boolean = false;
	private _cleanupHandlers: Map<string, () => void | Promise<void>> = new Map();
	startedAt: number | null = null;
	lastActive: number | null = null;
	countdownSeconds: number | null = null;
	countdownHandle: ReturnType<typeof setTimeout> | null = null;
	countdownIntervalHandle: ReturnType<typeof setInterval> | null = null;
	countdownExpiryHandle: ReturnType<typeof setTimeout> | null = null;
	/** KioWare's remaining time at the last countdown tick; a jump above it means the customer touched the screen. */
	private _countdownLastRemainingMs = 0;
	monitoringIntervalHandle: ReturnType<typeof setInterval> | null = null;
	private _countdownThresholdMs: number | null = null;
	private _assumedSessionTimeLimitTotalMs: number | null = null;
	private _isTimerEnabled: boolean;
	private readonly _defaultCountdownThresholdRatio = 0.25; // 25% of total session time
	private _skipCountdownModal: boolean = false;
	private _timeoutNavigationCallback: (() => void) | null = null;
	private _countdownContinueCallback: (() => void) | null = null;
	private _countdownExpireCallback: (() => void) | null = null;

	constructor() {
		makeAutoObservable(this);
		this._isTimerEnabled = import.meta.env.VITE_DISABLE_SESSION_TIMER !== 'true';
	}

	get sessionId() {
		return this._sessionId;
	}

	get legacyId() {
		return this._legacyId;
	}

	get isTimerEnabled() {
		return this._isTimerEnabled;
	}

	get isKioAppAvailable() {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		return !!(window as any).KioApp;
	}

	get skipCountdownModal() {
		return this._skipCountdownModal;
	}

	setSkipCountdownModal = (skip: boolean) => {
		this._skipCountdownModal = skip;
	};

	setTimeoutNavigationCallback = (callback: (() => void) | null) => {
		this._timeoutNavigationCallback = callback;
	};

	registerCleanup = (key: string, handler: () => void | Promise<void>) => {
		this._cleanupHandlers.set(key, handler);
		logger.debug.log(`Cleanup handler registered: ${key}`);
	};

	unregisterCleanup = (key: string) => {
		this._cleanupHandlers.delete(key);
		logger.debug.log(`Cleanup handler unregistered: ${key}`);
	};

	/**
	 * Resets the Kioware inactivity timer by calling KioApp.BeginSession().
	 * This extends the current session and prevents timeout. Never throws: callers reset the timer
	 * on the way to other work (a check read, a countdown continue) that must still happen.
	 */
	beginKioWareSession = () => {
		if (!this.isKioAppAvailable) return;
		try {
			KioApp.BeginSession();
		} catch (error) {
			logger.debug.error('[SessionStore] BeginSession failed:', error);
		}
	};

	/**
	 * Ends the KioWare session. Calls BeginSession() first so EndSession()
	 * always has an active session to end; without KioApp (plain browser)
	 * it reloads the page instead. This is the only place the client ends
	 * a KioWare session.
	 */
	endKioWareSession = (context: string) => {
		KioAppHelper('LogInfo', `[Availability] Calling EndSession (${context})`);
		if (this.isKioAppAvailable) {
			try {
				KioApp.BeginSession();
			} catch (error) {
				KioAppHelper('LogWarn', `[Availability] BeginSession failed before EndSession (${context}): ${error}`);
			}
			KioApp.EndSession();
		} else window.location.reload();
	};

	startLoggingSessionData = async () => {
		if (this.sessionId || this._isSessionEnding) return;
		this.beginKioWareSession();

		this._sessionId = ulid();
		await sqlite.setSetting('currentSessionId', this.sessionId!);
		this._legacyId = guid();
		this.lastActive = this.startedAt = Date.now();
		logger.logGeneral.log({}, `Session started: ${this.sessionId}`);
		this.startCountdownTriggerMonitoring();
	};

	/**
	 * Clears the shared currentSessionId setting. Must only ever be called
	 * from the Main customer-facing window — calling it from any other
	 * window would let the cash-recovery sweep treat an in-progress
	 * payment in another session as abandoned. See PageRenderer's
	 * isConfigLoaded-gated effect, which is the only caller and only fires
	 * once loadConfig() has already awaited sqlite.init(), so — like its
	 * sibling writers — this method never needs to initialize sqlite itself.
	 */
	clearActiveSessionId = async () => {
		await sqlite.setSetting('currentSessionId', '');
	};

	touch = async () => {
		if (this.sessionId && this.isActive) {
			this.beginKioWareSession();
			this.lastActive = Date.now();

			const { value: currentSessionId, failed } = await sqlite.tryGetSetting('currentSessionId');
			if (!failed && currentSessionId !== this.sessionId) await sqlite.setSetting('currentSessionId', this.sessionId);
		}
	};

	get assumedSessionTimeLimitTotalMs(): number | null {
		if (this._assumedSessionTimeLimitTotalMs !== null && this._assumedSessionTimeLimitTotalMs !== -1) return this._assumedSessionTimeLimitTotalMs;

		// Calculate assumed session time limit from KioInactivityTimer samples
		if (this.isKioAppAvailable) {
			this.beginKioWareSession();
			const sample1 = KioInactivityTimer.GetTimeRemainingMs();
			this.beginKioWareSession();
			const sample2 = KioInactivityTimer.GetTimeRemainingMs();
			this.beginKioWareSession();
			const sample3 = KioInactivityTimer.GetTimeRemainingMs();
			// Subtract 3 seconds to end session before Kioware forcefully reloads
			this._assumedSessionTimeLimitTotalMs = Math.max(sample1, sample2, sample3);
		}

		return this._assumedSessionTimeLimitTotalMs;
	}

	startCountdownTriggerMonitoring = () => {
		if (!this.isTimerEnabled || !this.isKioAppAvailable) return;

		if (this.assumedSessionTimeLimitTotalMs === null) {
			logger.logGeneral.error({}, 'Cannot start countdown monitoring: unable to determine session time limit', null, {
				message: 'assumedSessionTimeLimitTotalMs is null',
			});
			return;
		}

		if (this.monitoringIntervalHandle !== null) clearInterval(this.monitoringIntervalHandle);

		this.ensureCountdownThreshold(this.assumedSessionTimeLimitTotalMs);

		// Start monitoring interval
		this.monitoringIntervalHandle = setInterval(() => {
			if (!this.isActive || this.isShowingCountdown) return;

			const remainingMs = KioInactivityTimer.GetTimeRemainingMs();
			// -1 means KioWare's inactivity timer is not in use, so there is nothing to count down to.
			if (remainingMs < 0) return;
			if (this._countdownThresholdMs !== null && remainingMs <= this._countdownThresholdMs) {
				if (this.monitoringIntervalHandle !== null) {
					clearInterval(this.monitoringIntervalHandle);
					this.monitoringIntervalHandle = null;
				}
				this.startCountdown(Math.ceil(remainingMs / 1000));
			}
		}, 500);
	};

	/** Determines the countdown threshold once: custom seconds or percent from the config, else 25% of the session. */
	private ensureCountdownThreshold = (assumedSessionTimeLimitTotalMs: number) => {
		if (this._countdownThresholdMs !== null) return;

		const customTimeout = store.configStore.getCustomTimerTimeout;
		if (customTimeout?.type === 'seconds') this._countdownThresholdMs = customTimeout.value * 1000;
		else if (customTimeout?.type === 'percent') this._countdownThresholdMs = (assumedSessionTimeLimitTotalMs * customTimeout.value) / 100;
		else this._countdownThresholdMs = assumedSessionTimeLimitTotalMs * this._defaultCountdownThresholdRatio; // Default to 25%

		// Correct threshold if it exceeds total session time
		if (customTimeout?.type === 'seconds' && this._countdownThresholdMs > assumedSessionTimeLimitTotalMs) {
			this._countdownThresholdMs = assumedSessionTimeLimitTotalMs * this._defaultCountdownThresholdRatio;
			logger.logGeneral.warn(
				{},
				`Custom session timeout warning time prompt exceeds total session time, defaulting to ${this._defaultCountdownThresholdRatio * 100}% threshold`,
				{
					customTimeoutSeconds: customTimeout.value,
					assumedSessionTimeLimitTotalMs,
				}
			);
		}
	};

	/**
	 * Shows the session countdown now, at the configured threshold, instead of waiting for
	 * KioWare's remaining time to reach it. onContinue fires once if the customer keeps the
	 * session (Yes, or interaction detected), never on expiry or dismissCountdown().
	 * onExpire fires once when the countdown decides the visit is over, before the warning
	 * is hidden and before the session starts ending.
	 * Returns false when no countdown can run (no KioWare, KioWare's inactivity timer not in use, or the
	 * session timer is turned off).
	 */
	showCountdownNow = (onContinue?: () => void, onExpire?: () => void): boolean => {
		if (!this.isTimerEnabled || !this.isKioAppAvailable) return false;

		// KioWare reports -1 when its inactivity timer is not in use; no timeout means no countdown.
		if (KioInactivityTimer.GetTimeRemainingMs() < 0) return false;
		const assumedSessionTimeLimitTotalMs = this.assumedSessionTimeLimitTotalMs;
		if (assumedSessionTimeLimitTotalMs === null || assumedSessionTimeLimitTotalMs < 0) return false;
		this.ensureCountdownThreshold(assumedSessionTimeLimitTotalMs);

		if (this.monitoringIntervalHandle !== null) {
			clearInterval(this.monitoringIntervalHandle);
			this.monitoringIntervalHandle = null;
		}
		this.dismissCountdown();

		this._countdownContinueCallback = onContinue ?? null;
		this._countdownExpireCallback = onExpire ?? null;
		// KioWare was just kept alive, so its remaining time is well above the threshold; use it as the
		// interaction baseline or the first tick would read the gap as the customer touching the screen.
		this.startCountdown(Math.ceil(this._countdownThresholdMs! / 1000), KioInactivityTimer.GetTimeRemainingMs());
		return true;
	};

	startCountdown(initialSeconds: number, interactionBaselineMs: number = initialSeconds * 1000) {
		if (!this.isTimerEnabled || !this.isKioAppAvailable) return;

		this.countdownSeconds = Math.max(initialSeconds);
		this._countdownLastRemainingMs = interactionBaselineMs;
		// A countdown that starts at one second or less never ticks down to 1, so schedule its end now.
		if (this.countdownSeconds <= 0) this.scheduleCountdownExpiry(0);
		else if (this.countdownSeconds === 1) this.scheduleCountdownExpiry(500);

		this.countdownIntervalHandle = setInterval(() => {
			runInAction(() => {
				const currentRemainingMs = KioInactivityTimer.GetTimeRemainingMs();

				// If remaining time increased by more than 2 seconds, user must have interacted
				if (this.isInteractionSinceLastTick(currentRemainingMs)) {
					this.cancelCountdown();
					this.startCountdownTriggerMonitoring();
					return;
				}

				this._countdownLastRemainingMs = currentRemainingMs;

				if (this.countdownSeconds !== null) this.countdownSeconds--;
				if (this.countdownSeconds !== null && this.countdownSeconds <= 1) this.scheduleCountdownExpiry(500);
			});
		}, 1000);
	}

	/** If remaining time increased by more than 2 seconds, the customer must have interacted. */
	private isInteractionSinceLastTick = (currentRemainingMs: number) => currentRemainingMs > this._countdownLastRemainingMs + 2000;

	/** Schedules the end of the running countdown once; a dismiss or a Yes before it fires cancels it. */
	private scheduleCountdownExpiry = (delayMs: number) => {
		if (this.countdownExpiryHandle !== null) return;
		this.countdownExpiryHandle = setTimeout(this.expireCountdown, delayMs);
	};

	private expireCountdown = async () => {
		runInAction(() => {
			this.countdownExpiryHandle = null;
		});
		// A Yes, a detected interaction or a dismiss in the last half-second already closed it.
		if (this.countdownSeconds === null) return;
		// The last half-second has no tick of its own, so a touch in it is only visible here.
		if (this.isKioAppAvailable && this.isInteractionSinceLastTick(KioInactivityTimer.GetTimeRemainingMs())) {
			this.cancelCountdown();
			return;
		}

		const onExpire = this._countdownExpireCallback;
		try {
			onExpire?.();
		} catch (error) {
			// The visit must still end; otherwise the countdown keeps ticking and retries the failing callback.
			logger.logGeneral.error({}, '[SessionStore] countdown expire callback failed', error);
		}
		this.dismissCountdown();
		if (this._timeoutNavigationCallback) {
			this._timeoutNavigationCallback();
			// The visit continues on the screen the callback shows, so keep watching for idle time.
			this.startCountdownTriggerMonitoring();
		} else {
			await this.startEndSessionTasks();
		}
	};

	/** The customer kept the session: Yes on the countdown, or interaction detected. Fires the one-shot onContinue and resumes idle monitoring. */
	cancelCountdown = () => {
		const onContinue = this._countdownContinueCallback;
		this.dismissCountdown();
		try {
			onContinue?.();
		} catch (error) {
			logger.logGeneral.error({}, '[SessionStore] countdown continue callback failed', error);
		} finally {
			this.startCountdownTriggerMonitoring();
		}
	};

	/** Closes the countdown without counting it as the customer continuing. Does not resume idle monitoring. */
	dismissCountdown = () => {
		runInAction(() => {
			this.countdownSeconds = null;
			this._countdownContinueCallback = null;
			this._countdownExpireCallback = null;
			if (this.countdownIntervalHandle !== null) {
				clearInterval(this.countdownIntervalHandle);
				this.countdownIntervalHandle = null;
			}
			if (this.countdownExpiryHandle !== null) {
				clearTimeout(this.countdownExpiryHandle);
				this.countdownExpiryHandle = null;
			}
		});
	};

	startEndSessionTasks = async () => {
		await sqlite.setSetting('currentSessionId', '');

		if (!this._sessionId) {
			await sqlite.close();
			this.endKioWareSession('no customer session');
			return;
		}

		if (this._isSessionEnding) {
			logger.debug.log('Session is already ending, ignoring duplicate call.');
			return;
		}

		runInAction(() => {
			this._isSessionEnding = true;
		});

		KioAppHelper('LogInfo', `[Availability] Visit end started (session ${this._sessionId})`);

		store.uiStore.setBackEnabled(false);

		this.dismissCountdown();

		store.uiStore.showLoader();

		this.beginKioWareSession();

		if (import.meta.env.DEV) await new Promise((resolve) => setTimeout(resolve, 3000));

		// Run cleanup handlers
		await logger.logGeneral.log({}, `Running ${this._cleanupHandlers.size} cleanup handlers before session end`);
		const cleanupPromises: Promise<void>[] = [];
		this._cleanupHandlers.forEach(async (handler, key) => {
			try {
				logger.debug.log(`Executing cleanup for: ${key}`);
				const result = handler();
				if (result instanceof Promise) {
					cleanupPromises.push(
						result.catch(async (error) => {
							await logger.logGeneral.error({}, `Async cleanup handler failed for ${key}:`, error);
						})
					);
				}
			} catch (error) {
				await logger.logGeneral.error({}, `Cleanup handler failed for ${key}:`, error);
			}
		});

		if (cleanupPromises.length > 0) {
			logger.debug.log(`Waiting for ${cleanupPromises.length} async cleanup handlers`);
			await Promise.race([
				Promise.allSettled(cleanupPromises),
				new Promise((resolve) => setTimeout(resolve, 60000)), // 60 second cutoff timeout
			]);
		}

		this._cleanupHandlers.clear();

		// Post session
		try {
			await logger.logGeneral.log({}, `Ending session: ${this.sessionId}`);
			await this.postKioskSession();
		} catch (error) {
			await logger.logGeneral.error({}, 'Error ending session:', error);
		}

		// Send digital receipt if requested
		if (store.accountStore.receiptType === ReceiptType.Email || store.accountStore.receiptType === ReceiptType.Text)
			try {
				await logger.logGeneral.log(
					{},
					`Digital receipt requested: ${store.accountStore.receiptType} to ${store.accountStore.digitalReceiptDestination?.email ?? store.accountStore.digitalReceiptDestination?.phone}`
				);
				await this.legacyPostGetReceipt();
				// This needs a little bit more time
				await new Promise((resolve) => setTimeout(resolve, 1000));
			} catch (error) {
				await logger.logGeneral.error({}, 'Error after digital receipt submission', error);
			}

		await sqlite.close();

		this.endKioWareSession(`session ${this._sessionId}`);
	};

	get isActive() {
		return !!this.sessionId && !this._isSessionEnding;
	}

	get timeSinceLastActive(): number | null {
		return this.lastActive ? Date.now() - this.lastActive : null;
	}

	get isShowingCountdown(): boolean {
		return !this._skipCountdownModal && this.countdownSeconds !== null && this.countdownSeconds > 0;
	}

	private postKioskSession = async () => {
		const sessionData = structuredClone({
			id: this._sessionId!,
			legacyId: this._legacyId,
			kioskId: store.kioskStore.kioskId,
			startedAt: new Date(this.startedAt!),
			lastActivityAt: new Date(this.lastActive!),
			selectedLanguage: enumHelper.getSupportedLanguage(store.configStore.currentLanguage),
			selectedReceiptOptions: undefined,
			companyId: store.configStore.companyId,
			legacyLanguage: store.configStore.currentLanguageLegacyString,
		});

		await sqlite.createApiCall({
			id: ulid().toUpperCase(),
			timestamp: new Date(),
			requestData: JSON.stringify(sessionData),
			type: ApiCallServiceType.KioskSession,
		});

		await sqlite.createApiCall({
			id: ulid().toUpperCase(),
			timestamp: new Date(),
			requestData: JSON.stringify(sessionData),
			type: ApiCallServiceType.LegacyKioskSession,
		});
	};

	legacyPostNavUrl = async (pageId: string) => {
		if (!this.isActive) return;

		const navSession = {
			kioskID: store.kioskStore.kioskId,
			customerID: store.configStore.companyId,
			url: pageId,
			sessionGUID: this._legacyId!,
			timeStamp: new Date().toISOString(),
			passKey: await passKeyHelper.generatePassKey(),
			language: store.configStore.currentLanguageLegacyString,
		};

		await sqlite.createApiCall({
			id: ulid().toUpperCase(),
			timestamp: new Date(),
			requestData: JSON.stringify(navSession),
			type: ApiCallServiceType.NavSession,
		});
	};

	legacyPostGetReceipt = async () => {
		const recipient = store.accountStore.digitalReceiptDestination!.email || store.accountStore.digitalReceiptDestination!.phone!;
		if (!recipient) return null;

		await sqlite.createApiCall({
			id: ulid().toUpperCase(),
			timestamp: new Date(),
			requestData: JSON.stringify({
				dynatouchGUID: this._legacyId!,
				email: recipient,
			}),
			type: ApiCallServiceType.DigitalReceipt,
		});

		return true;
	};
}
